import { useState } from 'react'
import { Minus, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { AsyncMultiSelect, FilterBar } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingRows, Note } from '@/shared/ui/states'
import { RankedBars } from '@/shared/ui/charts'
import { useLoad } from '@/features/administration/hooks'
import { useCan } from '@/shared/auth/session'
import { listUnits } from '@/features/geography/api'
import { fmtDate, fmtNumber, fmtRelative } from '@/shared/lib/format'
import { leaderboard } from './api'
import {
  BADGES, BadgeChip, ROLES, ROLE_ORDER, TIER_ORDER, TierChip, scoreNumber, tierLabel,
} from './vocab'
import { MobilizerDrawer } from './MobilizerDrawer'

/**
 * The leaderboard.
 *
 * ## Five things that decide whether anyone trusts it
 *
 * 1. **It says when it was worked out.** Scores come from a nightly job, not
 *    from this request. Without `computedAt` on the screen, people assume it is
 *    live and report the lag as a bug.
 * 2. **It says when the rules changed.** Weights live in settings and somebody
 *    will edit them. A range spanning two versions is not a comparable trend, and
 *    this shows a warning rather than drawing a line through it.
 * 3. **A new entrant has no movement**, not a movement of zero. Zero means "held
 *    their place"; blank means "was not here last time".
 * 4. **Excluded people are counted, not hidden.** Somebody below the activity
 *    floor is reported in a number, because a coordinator looking for their team
 *    member needs to know why they are not in the list.
 * 5. **Ties share a rank and the next rank skips** — 1, 2, 2, 4. Breaking ties by
 *    id gives a stable but arbitrary winner, and the person who is always second
 *    notices.
 *
 * ## No phone numbers, not even masked ones
 *
 * This is the most-screenshotted screen in the feature.
 */

const LIMIT = 100

const PERIODS = [
  { value: 'WINDOW', label: 'Scoring window' },
  { value: 'DAY', label: 'Yesterday' },
  { value: 'WEEK', label: 'Last week' },
  { value: 'MONTH', label: 'Last month' },
]

export function MobilizerLeaderboardPage() {
  const canRead = useCan('mobilizer:read')
  const [params, setParams] = useSearchParams()

  const period = params.get('period') ?? 'WINDOW'
  const role = params.get('role') ?? ''
  const tier = params.get('tier') ?? ''
  const [unit, setUnit] = useState<{ value: string; label: string }[]>([])
  const [openId, setOpenId] = useState<string | null>(null)

  const patch = (next: Record<string, string | null>) => {
    const sp = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') sp.delete(k); else sp.set(k, v)
    }
    setParams(sp, { replace: true })
  }

  const unitId = unit[0]?.value
  const data = useLoad(
    () => (canRead
      // Explicit, because the server's default is 50 and a KPI reading "4,000
      // ranked" over a table of 50 with no pager makes a coordinator conclude
      // their team member is missing — the exact reading the `excluded` counter
      // exists to prevent.
      ? leaderboard({ period, role: role || undefined, tier: tier || undefined, unitId, limit: LIMIT })
      : Promise.resolve(null)),
    [canRead, period, role, tier, unitId])

  if (!canRead) return <Forbidden what="the leaderboard" />

  const d = data.data
  const rows = d?.rows ?? []
  const top = rows.slice(0, 3)
  const rest = rows.slice(3)
  const activeCount = [role, tier, unitId].filter(Boolean).length

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Leaderboard"
        description={d
          ? `${fmtDate(d.from)} to ${fmtDate(d.to)} · worked out ${d.computedAt ? fmtRelative(d.computedAt) : 'never'} · rules v${d.rulesVersion}`
          : 'Scores are worked out overnight, never as you look at them.'}
        right={(
          <button type="button" className="erp-btn" aria-label="Refresh"
                  onClick={() => void data.reload()}>
            <RefreshCw className="h-3.5 w-3.5" /> <span className="axis-hide-sm">Refresh</span>
          </button>
        )}
      />

      {d?.rulesChangedInRange && (
        <Note tone="bad">
          The scoring weights changed inside this range, so the rows were not all computed
          the same way. Compare them with that in mind, or narrow the range to one side of
          the change. This is not a rendering problem — the numbers really are incomparable.
        </Note>
      )}

      <KpiStrip>
        <MetricCard label="Ranked" value={fmtNumber(d?.eligible ?? 0)}
                    sub="met the activity floor" accent />
        <MetricCard label="Not ranked" value={fmtNumber(d?.excluded ?? 0)}
                    sub="too little activity to rank fairly" />
        <MetricCard label="Top score"
                    value={rows[0] ? (scoreNumber(rows[0].score) ?? 0).toLocaleString('en-KE') : '—'}
                    sub={rows[0]?.fullName ?? 'nobody yet'} />
        <MetricCard label="Flagged activity"
                    value={fmtNumber(rows.reduce((a, r) => a + r.flagged, 0))}
                    sub="on the rows shown, excluded from scores" />
      </KpiStrip>

      <FilterBar activeCount={activeCount}>
        <select className="erp-input" value={period} aria-label="Period"
                onChange={(e) => patch({ period: e.target.value })}>
          {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <select className="erp-input" value={role} aria-label="Role"
                onChange={(e) => patch({ role: e.target.value })}>
          <option value="">All roles</option>
          {ROLE_ORDER.map((c) => <option key={c} value={c}>{ROLES[c].label}</option>)}
        </select>
        <select className="erp-input" value={tier} aria-label="Tier"
                onChange={(e) => patch({ tier: e.target.value })}>
          <option value="">All tiers</option>
          {TIER_ORDER.map((t) => <option key={t} value={t}>{tierLabel(t)}</option>)}
        </select>
        <AsyncMultiSelect
          label="Within"
          selected={unit}
          onChange={(next) => setUnit(next.slice(-1))}
          placeholder="Narrow to a county or ward"
          search={async (term) => {
            const found = await listUnits({ q: term } as never, 0, 10)
            return found.content.map((u) => ({ value: u.id, label: u.name, sub: u.code }))
          }}
        />
      </FilterBar>

      <ErrorStrip message={data.error} />

      {top.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          {top.map((r) => (
            <button
              key={r.mobilizerId}
              type="button"
              onClick={() => setOpenId(r.mobilizerId)}
              className="erp-card px-4 py-3 text-left hover:bg-muted/30 min-w-0"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="erp-metric text-erp-xs text-muted-foreground">#{r.rank}</span>
                <TierChip code={r.tier} />
              </div>
              <div className="text-erp-md font-semibold truncate mt-1">{r.fullName}</div>
              <div className="text-erp-xs text-muted-foreground truncate">
                {ROLES[r.roleCode]?.label ?? r.roleCode} · {r.primaryUnitName ?? 'no unit'}
              </div>
              <div className="erp-metric text-erp-2xl font-semibold mt-2">
                {(scoreNumber(r.score) ?? 0).toLocaleString('en-KE')}
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {r.badges.slice(0, 3).map((b) => <BadgeChip key={b} code={b} />)}
              </div>
            </button>
          ))}
        </div>
      )}

      <Panel
        bodyClassName="p-0"
        title={rows.length ? `Ranked (${rows.length}${(d?.eligible ?? 0) > rows.length ? ` of ${d?.eligible}` : ''})` : undefined}
        description={(d?.eligible ?? 0) > rows.length
          ? `Showing the top ${rows.length}. Narrow by unit or role to see further down.`
          : undefined}
      >
        <ErpTable head={<>
          <Th className="w-12 text-right">#</Th>
          <Th>Mobilizer</Th>
          <Th>Tier</Th>
          <Th className="text-right">Score</Th>
          <Th className="w-20 text-center">Move</Th>
          <Th className="text-right">Activity</Th>
          <Th className="text-right">Registered</Th>
          <Th className="text-right">Verified</Th>
          <Th className="text-right">Contacted</Th>
          <Th className="text-right">Tasks</Th>
          <Th className="text-right">Streak</Th>
          <Th>Badges</Th>
        </>}>
          {data.loading && !d ? (
            <LoadingRows colSpan={12} label="Reading last night's numbers…" />
          ) : top.length === 0 ? (
            <EmptyRow colSpan={12}
                      message="Nobody is ranked yet. Scores appear the morning after the first day of activity." />
          ) : rest.length === 0 ? (
            <EmptyRow colSpan={12}
                      message="Everyone ranked is on the podium above." />
          ) : rest.map((r) => (
            <tr key={r.mobilizerId} className="hover:bg-muted/40 cursor-pointer"
                data-rank={String(r.rank)}
                onClick={() => setOpenId(r.mobilizerId)}>
              <Td className="text-right erp-metric">{r.rank}</Td>
              <Td>
                <span className="block font-medium truncate">{r.fullName}</span>
                <span className="block text-erp-xs text-muted-foreground truncate">
                  {r.primaryUnitName ?? 'no unit'}
                </span>
              </Td>
              <Td><TierChip code={r.tier} /></Td>
              <Td className="text-right erp-metric">
                {(scoreNumber(r.score) ?? 0).toLocaleString('en-KE')}
              </Td>
              <Td className="text-center"><Movement value={r.movement} /></Td>
              <Td className="text-right erp-metric" title={r.flagged ? `${r.flagged} flagged and not scored` : undefined}>
                {fmtNumber(r.activities)}
                {r.flagged > 0 && <span className="text-muted-foreground"> ({r.flagged}⚑)</span>}
              </Td>
              <Td className="text-right erp-metric">{fmtNumber(r.votersRegistered)}</Td>
              <Td className="text-right erp-metric">{fmtNumber(r.votersVerified)}</Td>
              <Td className="text-right erp-metric">{fmtNumber(r.votersContacted)}</Td>
              <Td className="text-right erp-metric">{fmtNumber(r.tasksCompleted)}</Td>
              <Td className="text-right erp-metric">{r.streakDays || '—'}</Td>
              <Td>
                <span className="flex flex-wrap gap-1">
                  {r.badges.map((b) => <BadgeChip key={b} code={b} />)}
                </span>
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Tier distribution"
               description="Thresholds are a setting, not a constant — a coordinator can move them without a release.">
          <RankedBars
            points={TIER_ORDER.map((t) => ({
              label: tierLabel(t),
              value: d?.tierDistribution?.[t] ?? 0,
            })).filter((p) => p.value > 0)}
            emptyMessage="Nobody is tiered yet."
          />
        </Panel>

        <Panel title="What earns a badge"
               description="Written down, because a badge nobody can explain is a badge people assume is random.">
          <dl className="space-y-1.5">
            {(Object.keys(BADGES) as (keyof typeof BADGES)[])
              .filter((b) => b !== 'UNKNOWN')
              .map((b) => (
                <div key={b} className="flex items-start gap-2">
                  <dt className="shrink-0"><BadgeChip code={b} /></dt>
                  <dd className="text-erp-xs text-muted-foreground min-w-0">{BADGES[b].earns}</dd>
                </div>
              ))}
          </dl>
        </Panel>
      </div>

      {(d?.excluded ?? 0) > 0 && (
        <Note tone="info">
          {d?.excluded} {d?.excluded === 1 ? 'person is' : 'people are'} not ranked because
          they are below the activity floor. That is deliberate — somebody with two
          activities and a lucky weighting is not the top mobilizer in the county — and
          they are counted here rather than disappearing without explanation.
        </Note>
      )}

      {/* Keyed on the subject — see the note in RosterPage. */}
      <MobilizerDrawer
        key={openId ?? 'closed'}
        id={openId}
        onClose={() => setOpenId(null)}
        onChanged={() => void data.reload()}
        onOpenOther={(id) => setOpenId(id)}
      />
    </div>
  )
}

/** Blank for a new entrant, a dash for "held their place", an arrow otherwise. */
function Movement({ value }: { value?: number | null }) {
  if (value === null || value === undefined) {
    return <span className="text-erp-xs text-muted-foreground" title="New — no previous position">new</span>
  }
  if (value === 0) {
    return <Minus className="h-3.5 w-3.5 inline text-muted-foreground" aria-label="No change" />
  }
  const up = value > 0
  return (
    <span className={up
      ? 'inline-flex items-center gap-0.5 text-emerald-700 dark:text-emerald-400 text-erp-xs'
      : 'inline-flex items-center gap-0.5 text-red-700 dark:text-red-400 text-erp-xs'}>
      {up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {Math.abs(value)}
    </span>
  )
}
